import { TouchPanel } from 'nots-window-renderer/lib';
import QueryString from 'query-string';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { Container } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUp from '@material-ui/icons/KeyboardArrowUp';

import { CommunicationMessageTypes, RolesMap } from '../../../../../helpers/constants';
import { isEmpty } from '../../../../../helpers/utils';
import useCommunicator from '../../../../../hooks/useCommunicator';
import actionService from '../../../../../store/actions.service';
import {
    sendToParent_AC, toggleButtonClick_Ac, touchPanelButtonClicked_AC
} from '../../../../../store/actions/executor.actions';
import { IKeyboardMessage } from '../../../../../types/communicator-types';
import useLogger from '../../../../common-components/hooks/useLogger';
import CornerScrews from '../CornerScrews';
import Led from '../Led';
import s80json from '../TouchPanel/S80_TSD_Definition.json';

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100%',
    border: '6px solid rgb(50, 50, 50)',
    padding: '0',
    borderRadius: '5px',
    background:
      'linear-gradient(to bottom right, rgb(56, 56, 56) 0%, rgb(60, 60, 60) 100%)',
    boxShadow: '0 0 6px black',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  outerContainer: {
    display: 'flex',
    height: '100%',
    padding: '25px 0px 25px 40px',
    borderRadius: '3px',
  },
  innerContainer: {
    flex: 18,
    backgroundColor: 'rgb(50, 50, 50)',
    height: '100%',
    borderRadius: '16px',
    padding: '20px',
  },
  tsd: {
    backgroundColor: 'rgb(19, 24, 62)',
    height: '100%',
    borderRadius: '3px',
    boxShadow: '0 0 1px white',
    padding: '0px 15px',
  },
  indicatorsContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
    alignItems: 'center',
  },
  indicator: {
    margin: '0 auto',
    width: '24px',
    height: '6px',
    backgroundColor: '#7dbb01',
    border: '0.1px solid white',
    borderRadius: '3px',
    marginBottom: '5px',
    boxShadow:
      'rgba(0, 0, 0, 0.2) 0 0 7px 1px, inset #88cb01 0 0 9px, #89FF00 0 0px 12px',
  },
  arrowContainer: {
    display: 'flex',
    border: '1.5px solid white',
    borderRadius: '5px',
    margin: '5px',
  },
  powerContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  text: {
    fontSize: '8px',
    color: 'white',
  },
  demoOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    height: '100vh',
    width: '100vw',
  },
}));

interface ITSDProps {
  active: boolean;
}

const S80_TSD_DEFINITION = 'S80_TSD_DEFINITION';
const S80_TSD_DEFINITION_V2 = 'S80_TSD_DEFINITION_V2';
const TSD: React.FC<ITSDProps> = ({ active = false }) => {
  const jsonDefintion = useSelector(
    (state: any) => state.resources[S80_TSD_DEFINITION]
  );
  const jsonDefintionV2 = useSelector(
    (state: any) => state.resources[S80_TSD_DEFINITION_V2]
  );
  // A lesson opts into the new layout by attaching the V2 definition resource.
  // When absent, behaviour is identical to before so existing lessons are unaffected.
  const activeDefId = jsonDefintionV2
    ? S80_TSD_DEFINITION_V2
    : S80_TSD_DEFINITION;
  const communicator = useCommunicator<IKeyboardMessage>(
    CommunicationMessageTypes.KeyboardEvent
  );
  const classes = useStyles();
  const dispatch = useDispatch();
  const s80 = useSelector((state: any) => state.executor.s80);
  const executor = useSelector((state: any) => state.executor);
  const context = useSelector((state: any) => state.executor.context);
  const location = useLocation();
  const { mode } = QueryString.parse(location.search);
  const onTouchPanelClick = ({ id }: { id: any }) => {
    dispatch(
      sendToParent_AC(touchPanelButtonClicked_AC(id, activeDefId))
    );
  };
  useLogger('TSD/index.tsx', 'S80 TSD');

  /*
   * Analysis by reading the code is this: This function is only responsible
   * for toggling the button and marking the checkpoint. The actual action that
   * gets submitted to the executor is handled by onClick Action flow.
   * Since a toggle button action triggers both the toggle and the click action,
   * we can get away with this. [Verify this with the actual code flow]
   */
  const onToggleButtonClick = (
    variable: string,
    activeIndex: number,
    options: Array<any>
  ) => {
    const nextIndex = (activeIndex + 1) % options.length;
    const selectedOption = options[nextIndex];
    // toggle the state here and mark checkpoint
    dispatch(toggleButtonClick_Ac(variable, selectedOption.key));
    // send to parent so that the parent also marks item for checkpoint
    dispatch(
      sendToParent_AC(toggleButtonClick_Ac(variable, selectedOption.key))
    );
  };

  const onKeyboardAction = (payload: any) => {
    communicator?.postMessage(payload);
  };
  return (
    <Container className={classes.root}>
      {(mode === 'demo' || !isEmpty(executor.multimediaText)) && (
        <div className={classes.demoOverlay}></div>
      )}
      <CornerScrews noOfScrews={4} />
      <Container className={classes.outerContainer}>
        <Container className={classes.innerContainer}>
          <div className={classes.tsd}>
            <TouchPanel
              onClick={onTouchPanelClick}
              onToggleButtonClick={onToggleButtonClick}
              s80json={jsonDefintionV2 ?? jsonDefintion ?? s80json}
              actionService={actionService}
              dispatch={dispatch}
              content={touchPanelContent(s80, mode, executor)}
              onKeyboardAction={onKeyboardAction}
            />
          </div>
        </Container>
        <Container className={classes.indicatorsContainer}>
          {/* <Container className={classes.indicatorsInnerContainer}> */}
          <Led text="POWER" ledOn={active} offColor={'GREEN'} />
          <Led offColor={'YELLOW'} text="OUT R TEMP" />
          <Led offColor={'RED'} text="FAULT" />
          <div className={classes.arrowContainer}>
            <KeyboardArrowUp htmlColor="white" />
          </div>
          <span className={classes.text}>BRIGHTNESS</span>
          <div className={classes.arrowContainer}>
            <KeyboardArrowDown htmlColor="white" />
          </div>
          {/* </Container> */}
        </Container>
      </Container>
      <CornerScrews noOfScrews={4} />
    </Container>
  );
};

const touchPanelContent = (s80: any, mode: any, executor: any) => ({
  activeRole: executor.activeRole,
  activeRoleLongText: RolesMap[executor.activeRole?.toUpperCase()],
  residentRoles: s80.residentRoles,
  activeVabArray: s80.activeVabArray,
  modeSystem: s80.modeSystem,
  modeConsole: s80.modeConsole,
  touchPanelVariables: s80.touchPanelVariables,
  mode: mode,
  tutorialAreaText: s80.tsdTutorialAreaText,
});

export default TSD;
